import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Edit, Trash2, Save, X } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ManagePartners = () => {
  const { token } = useAuth();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    title: '',
    specialty: '',
    bio: '',
    image: '',
    order: 0
  });

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    try {
      const response = await axios.get(`${API}/partners`);
      setPartners(response.data);
    } catch (error) {
      console.error('Error fetching partners:', error);
      toast.error('Failed to load partners');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (partner) => {
    setEditingId(partner.id);
    setFormData({
      name: partner.name,
      title: partner.title,
      specialty: partner.specialty,
      bio: partner.bio,
      image: partner.image,
      order: partner.order
    });
  };

  const handleSave = async (partnerId) => {
    try {
      await axios.put(`${API}/admin/partners/${partnerId}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Partner updated successfully');
      setEditingId(null);
      fetchPartners();
    } catch (error) {
      console.error('Error updating partner:', error);
      toast.error('Failed to update partner');
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setFormData({ name: '', title: '', specialty: '', bio: '', image: '', order: 0 });
  };

  const handleDelete = async (partnerId) => {
    if (!window.confirm('Are you sure you want to delete this partner?')) return;
    
    try {
      await axios.delete(`${API}/admin/partners/${partnerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Partner deleted successfully');
      fetchPartners();
    } catch (error) {
      console.error('Error deleting partner:', error);
      toast.error('Failed to delete partner');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center space-x-4">
          <Link to="/admin/dashboard">
            <Button variant="outline" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Manage Partners</h1>
            <p className="text-sm text-gray-600">Edit partner information and photos</p>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="space-y-6">
          {partners.map((partner) => (
            <Card key={partner.id} className="overflow-hidden">
              {editingId === partner.id ? (
                <CardContent className="p-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div>
                        <Label>Name *</Label>
                        <Input
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          placeholder="Full name"
                        />
                      </div>
                      <div>
                        <Label>Title *</Label>
                        <Input
                          value={formData.title}
                          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                          placeholder="e.g., Senior Tax Consultant"
                        />
                      </div>
                      <div>
                        <Label>Specialty *</Label>
                        <Input
                          value={formData.specialty}
                          onChange={(e) => setFormData({ ...formData, specialty: e.target.value })}
                          placeholder="e.g., Personal & Business Tax Services"
                        />
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <Label>Photo URL *</Label>
                        <Input
                          value={formData.image}
                          onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                          placeholder="https://example.com/photo.jpg"
                        />
                        {formData.image && (
                          <img src={formData.image} alt="Preview" className="mt-2 w-32 h-32 rounded-lg object-cover" />
                        )}
                      </div>
                      <div>
                        <Label>Bio *</Label>
                        <Textarea
                          value={formData.bio}
                          onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                          placeholder="Brief professional bio"
                          rows={4}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="flex space-x-4 mt-6">
                    <Button onClick={() => handleSave(partner.id)} className="bg-teal-600 hover:bg-teal-700">
                      <Save className="h-4 w-4 mr-2" />
                      Save Changes
                    </Button>
                    <Button onClick={handleCancel} variant="outline">
                      <X className="h-4 w-4 mr-2" />
                      Cancel
                    </Button>
                  </div>
                </CardContent>
              ) : (
                <div className="flex">
                  <img
                    src={partner.image}
                    alt={partner.name}
                    className="w-48 h-48 object-cover"
                  />
                  <CardContent className="flex-1 p-6">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900">{partner.name}</h3>
                        <p className="text-teal-600 font-semibold">{partner.title}</p>
                        <p className="text-gray-600 text-sm mt-1">{partner.specialty}</p>
                        <p className="text-gray-700 mt-3 leading-relaxed">{partner.bio}</p>
                      </div>
                      <div className="flex space-x-2">
                        <Button onClick={() => handleEdit(partner)} size="sm" variant="outline">
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          onClick={() => handleDelete(partner.id)} 
                          size="sm" 
                          variant="outline"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </div>
              )}
            </Card>
          ))}
        </div>

        <Card className="mt-8 bg-gray-50 border-dashed border-2">
          <CardContent className="p-8 text-center">
            <p className="text-gray-600 mb-4">Need to add a new partner?</p>
            <p className="text-sm text-gray-500">Contact your developer to add new team members to the system.</p>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default ManagePartners;
